// Utility để quản lý tiến trình thi của các dạng bài thi

import { dangbaiService } from 'services/dangbaiService';

const DEFAULT_EXAM_TYPES = ['NGUPHAP', 'NGHE', 'DOC', 'NOI', 'VIET'];
const STORAGE_KEY = 'exam_progress';
const STORAGE_KEY_TYPES = 'exam_types_ordered';

/**
 * Load danh sách dạng bài từ DB (order by stt) và cache vào localStorage.
 * Nếu lỗi/không có dữ liệu hợp lệ thì fallback DEFAULT_EXAM_TYPES.
 */
export const loadExamTypesFromDb = async () => {
    try {
        const res = await dangbaiService.getList();
        const list = res?.data?.data || res?.data?.data?.data; // tolerate various shapes
        const types = (Array.isArray(list) ? list : [])
            .map(x => x?.ma)
            .filter(Boolean);

        if (types.length > 0) {
            localStorage.setItem(STORAGE_KEY_TYPES, JSON.stringify(types));
            return types;
        }
    } catch (e) {
        // ignore
    }
    return DEFAULT_EXAM_TYPES;
};

export const getExamTypes = () => {
    const stored = localStorage.getItem(STORAGE_KEY_TYPES);
    if (!stored) return DEFAULT_EXAM_TYPES;
    try {
        const types = JSON.parse(stored);
        return Array.isArray(types) && types.length ? types : DEFAULT_EXAM_TYPES;
    } catch (e) {
        return DEFAULT_EXAM_TYPES;
    }
};

/**
 * Khởi tạo trạng thái thi cho một đề thi
 * @param {string|number} dethiId - ID của đề thi
 */
export const initExamProgress = (dethiId) => {
    // Chuyển dethiId thành string để so sánh nhất quán
    const dethiIdStr = String(dethiId);

    // Luôn reset completedTypes khi khởi tạo progress mới
    // XÓA progress cũ trước khi tạo mới để đảm bảo không còn dữ liệu cũ
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem('lastTimerType');

    const progress = {
        dethiId: dethiIdStr,
        completedTypes: [], // Luôn bắt đầu với mảng rỗng
        startTime: new Date().toISOString()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
    return progress;
};

/**
 * Lấy trạng thái thi hiện tại
 */
export const getExamProgress = () => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    try {
        const progress = JSON.parse(stored);
        // Validate progress structure
        if (!progress.dethiId || !Array.isArray(progress.completedTypes)) {
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }
        return progress;
    } catch (e) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
    }
};

/**
 * Đánh dấu một dạng thi đã hoàn thành
 * @param {string} examType - Loại dạng thi (NGUPHAP, NGHE, DOC, NOI, VIET)
 */
export const markExamTypeCompleted = (examType) => {
    const progress = getExamProgress();
    if (!progress) {
        return null;
    }

    // Validate examType
    const EXAM_TYPES = getExamTypes();
    if (!EXAM_TYPES.includes(examType)) {
        return progress;
    }

    if (!progress.completedTypes.includes(examType)) {
        progress.completedTypes.push(examType);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
    }
    return progress;
};

/**
 * Kiểm tra xem một dạng thi đã hoàn thành chưa
 * @param {string} examType - Loại dạng thi
 */
export const isExamTypeCompleted = (examType) => {
    const progress = getExamProgress();
    if (!progress) return false;
    return progress.completedTypes.includes(examType);
};

/**
 * Lấy dạng thi tiếp theo cần thi
 * @returns {string|null} - Dạng thi tiếp theo hoặc null nếu đã thi hết
 */
export const getNextExamType = () => {
    const progress = getExamProgress();
    const EXAM_TYPES = getExamTypes();
    if (!progress) {
        return EXAM_TYPES[0]; // Nếu chưa có progress, trả về dạng đầu tiên
    }

    // Loại bỏ các dạng không hợp lệ khỏi completedTypes (nếu có)
    const validCompletedTypes = progress.completedTypes.filter(type => EXAM_TYPES.includes(type));
    if (validCompletedTypes.length !== progress.completedTypes.length) {
        progress.completedTypes = validCompletedTypes;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
    }

    for (const examType of EXAM_TYPES) {
        const isCompleted = progress.completedTypes.includes(examType);
        if (!isCompleted) {
            return examType;
        }
    }
    return null; // Đã thi hết tất cả
};

/**
 * Kiểm tra xem đã thi hết tất cả các dạng chưa
 */
export const isAllExamTypesCompleted = () => {
    const progress = getExamProgress();
    if (!progress) return false;
    const EXAM_TYPES = getExamTypes();
    return progress.completedTypes.length === EXAM_TYPES.length;
};

/**
 * Reset trạng thái thi (khi bắt đầu đề thi mới)
 */
export const resetExamProgress = () => {
    localStorage.removeItem(STORAGE_KEY);
};

/**
 * Debug: Hiển thị trạng thái progress hiện tại
 */
export const debugExamProgress = () => {
    const progress = getExamProgress();
    if (!progress) {
        return;
    }
    const nextType = getNextExamType();
};

/**
 * Lấy route tương ứng với dạng thi
 * @param {string} examType - Loại dạng thi
 */
export const getExamTypeRoute = (examType) => {
    if (examType === 'NGUPHAP') return '/startexamgrammar';
    if (examType === 'NGHE') return '/startexam';
    if (examType === 'DOC') return '/startexam';
    if (examType === 'NOI') return '/startexamnoi';
    if (examType === 'VIET') return '/startexamviet';
    return '/startexam';
};

